import { T, TN, esc } from '../shared/client';
import { SNAP, overlaps, tableBox } from '../../src/domain/layout';

/**
 * Floor plan editor: tables plus walls, doors, windows, pillars and labels,
 * with drag, resize, rotate, multi-select, alignment, zoom and undo. It edits
 * the draft document in place (pixels, degrees) and tells its host when
 * something changed so the draft autosaves. Nothing reaches the engine until
 * the draft is published.
 */
export interface LayoutHost {
  doc(): any;
  changed(): void;
  toast(msg: string, isErr?: boolean): void;
}

type FeatureKind = 'wall' | 'door' | 'window' | 'pillar' | 'label';
type Tool = 'select' | 'wall' | 'pan';
type Align = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';

const AREA_COLORS = ['#4aa3df', '#5ec27b', '#e0a43a', '#b07be0', '#e06a6a', '#4ac7c0'];
const MIN_SIZE = 36;
const MAX = 5000;
const ZOOMS = [0.4, 0.5, 0.65, 0.8, 1, 1.25, 1.5, 2];
/** Table shapes offered in the Elements panel; sizes are pixels on the plan. */
interface ElementSpec { id: string; shape: 'rect' | 'round' | 'diamond'; seats: number; w: number; h: number }
const ELEMENTS: ElementSpec[] = [
  { id: 'round2', shape: 'round', seats: 2, w: 60, h: 60 },
  { id: 'square2', shape: 'rect', seats: 2, w: 60, h: 60 },
  { id: 'diamond2', shape: 'diamond', seats: 2, w: 60, h: 60 },
  { id: 'round4', shape: 'round', seats: 4, w: 84, h: 84 },
  { id: 'rect4', shape: 'rect', seats: 4, w: 84, h: 60 },
  { id: 'square4', shape: 'rect', seats: 4, w: 84, h: 84 },
  { id: 'round6', shape: 'round', seats: 6, w: 100, h: 100 },
  { id: 'rect6', shape: 'rect', seats: 6, w: 132, h: 60 },
  { id: 'diamond4', shape: 'diamond', seats: 4, w: 84, h: 84 },
  { id: 'round8', shape: 'round', seats: 8, w: 120, h: 120 },
  { id: 'rect8', shape: 'rect', seats: 8, w: 180, h: 66 },
  { id: 'rect10', shape: 'rect', seats: 10, w: 228, h: 70 },
  { id: 'stool', shape: 'round', seats: 1, w: 36, h: 36 },
  { id: 'rect12', shape: 'rect', seats: 12, w: 300, h: 72 },
];

interface Chair { x: number; y: number; rot: number }
/** Seat positions around a table, relative to its centre, in the table's own (unrotated) frame. */
function chairsFor(shape: string, w: number, h: number, seats: number): Chair[] {
  const out: Chair[] = [];
  const n = Math.max(0, Math.min(seats, 16));
  if (shape === 'round') {
    const r = Math.min(w, h) / 2 + 4;
    for (let i = 0; i < n; i += 1) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / Math.max(n, 1);
      out.push({ x: Math.cos(a) * r, y: Math.sin(a) * r, rot: (a * 180) / Math.PI + 90 });
    }
    return out;
  }
  const horizontal = w >= h;
  const first = Math.ceil(n / 2);
  const second = n - first;
  const along = horizontal ? w : h;
  const off = (horizontal ? h : w) / 2 + 4;
  const place = (count: number, sign: number) => {
    for (let i = 0; i < count; i += 1) {
      const pos = ((i + 0.5) / count) * along - along / 2;
      out.push(
        horizontal ? { x: pos, y: sign * off, rot: sign < 0 ? 0 : 180 } : { x: sign * off, y: pos, rot: sign < 0 ? 270 : 90 },
      );
    }
  };
  place(first, -1);
  place(second, 1);
  return out;
}

const uid = () => (crypto as any).randomUUID() as string;
const snap = (n: number, on: boolean) => (on ? Math.round(n / SNAP) * SNAP : Math.round(n));
const norm = (deg: number) => ((Math.round(deg) % 360) + 360) % 360;
const featureLabel = (k: FeatureKind) => T(`l.feature.${k}`);
const FEATURE_DEFAULT: Record<FeatureKind, { w: number; h: number }> = {
  wall: { w: 240, h: 10 }, door: { w: 60, h: 10 }, window: { w: 120, h: 8 }, pillar: { w: 28, h: 28 }, label: { w: 120, h: 28 },
};

interface Item {
  id: string;
  type: 'table' | 'feature';
  ref: any;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
}

interface Drag {
  kind: 'move' | 'resize' | 'rotate' | 'marquee' | 'wall' | 'pan';
  px: number;
  py: number;
  before: string;
  moved: boolean;
  origins: { id: string; x: number; y: number }[];
  leader?: string;
  id?: string;
  ow?: number;
  oh?: number;
  ox?: number;
  oy?: number;
  sx?: number;
  sy?: number;
  ex?: number;
  ey?: number;
  additive?: boolean;
}

const svg = (inner: string) =>
  `<svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
const ICONS: Record<string, string> = {
  undo: svg('<path d="M7 5 3.5 8.5 7 12"/><path d="M4 8.5h8a4 4 0 0 1 0 8H8"/>'),
  redo: svg('<path d="M13 5l3.5 3.5L13 12"/><path d="M16 8.5H8a4 4 0 0 0 0 8h4"/>'),
  'zoom-in': svg('<circle cx="8.5" cy="8.5" r="5.5"/><path d="m13 13 4 4M8.5 6v5M6 8.5h5"/>'),
  'zoom-out': svg('<circle cx="8.5" cy="8.5" r="5.5"/><path d="m13 13 4 4M6 8.5h5"/>'),
  fit: svg('<path d="M3 7V3h4M13 3h4v4M17 13v4h-4M7 17H3v-4"/>'),
  select: svg('<path d="M4 3l11 6.5-4.8 1.4L8.5 16z"/>'),
  pan: svg('<path d="M10 2v16M2 10h16M10 2 7.5 4.5M10 2l2.5 2.5M10 18l-2.5-2.5M10 18l2.5-2.5M2 10l2.5-2.5M2 10l2.5 2.5M18 10l-2.5-2.5M18 10l-2.5 2.5"/>'),
  'rot-left': svg('<path d="M4 9a6 6 0 1 1 1.8 4.3"/><path d="M4 4.5V9h4.5"/>'),
  'rot-right': svg('<path d="M16 9a6 6 0 1 0-1.8 4.3"/><path d="M16 4.5V9h-4.5"/>'),
  locate: svg('<circle cx="10" cy="10" r="3.5"/><path d="M10 2v3M10 15v3M2 10h3M15 10h3"/>'),
  'f-wall': svg('<rect x="2" y="8" width="16" height="4" rx="1"/>'),
  'f-door': svg('<path d="M3 16h14M4 16V4M4 4a12 12 0 0 1 12 12" stroke-dasharray="2 2"/>'),
  'f-window': svg('<rect x="3" y="7" width="14" height="6"/><path d="M10 7v6"/>'),
  'f-pillar': svg('<rect x="6" y="6" width="8" height="8" rx="1.5"/>'),
  'f-label': svg('<path d="M4 5h12M10 5v10M7 15h6"/>'),
};

/** A small picture of a table with its chairs, for the Elements panel. */
function elementIcon(el: ElementSpec): string {
  const size = 44;
  const pad = 9;
  const k = (size - pad * 2) / Math.max(el.w, el.h);
  const w = el.w * k;
  const h = el.h * k;
  const cx = size / 2;
  const cy = size / 2;
  const chairs = chairsFor(el.shape, el.w, el.h, el.seats)
    .map((c) => {
      const x = cx + c.x * k;
      const y = cy + c.y * k;
      return `<rect x="${(x - 4.5).toFixed(1)}" y="${(y - 1.6).toFixed(1)}" width="9" height="3.2" rx="1.4" fill="#3b3f46" transform="rotate(${Math.round(c.rot)} ${x.toFixed(1)} ${y.toFixed(1)})"/>`;
    })
    .join('');
  const body =
    el.shape === 'round'
      ? `<ellipse cx="${cx}" cy="${cy}" rx="${(w / 2).toFixed(1)}" ry="${(h / 2).toFixed(1)}" fill="#c7cacf"/>`
      : el.shape === 'diamond'
        ? `<rect x="${(cx - w / 2).toFixed(1)}" y="${(cy - h / 2).toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="3" fill="#c7cacf" transform="rotate(45 ${cx} ${cy})"/>`
        : `<rect x="${(cx - w / 2).toFixed(1)}" y="${(cy - h / 2).toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="3" fill="#c7cacf"/>`;
  return `<svg viewBox="0 0 ${size} ${size}" width="44" height="44" aria-hidden="true">${chairs}${body}</svg>`;
}

export class LayoutEditor {
  private el: HTMLElement | null = null;
  private planId = '';
  private selected = new Set<string>();
  private undo: string[] = [];
  private redo: string[] = [];
  private snapOn = true;
  private zoom = 1;
  private tool: Tool = 'select';
  private drag: Drag | null = null;
  /** A table combination selected on the canvas (exclusive with item selection). */
  private combo: string | null = null;

  constructor(private host: LayoutHost) {}

  // ------------------------------------------------------------ model helpers
  private get doc() {
    return this.host.doc();
  }
  private features(): any[] {
    if (!this.doc.features) this.doc.features = [];
    return this.doc.features;
  }
  private snapshot(): string {
    const d = this.doc;
    return JSON.stringify({
      tables: d.tables,
      features: this.features(),
      combos: d.combos,
      policies: d.shifts.map((x: any) => x.tablePolicy),
    });
  }
  private restore(json: string) {
    const s = JSON.parse(json);
    const d = this.doc;
    d.tables = s.tables;
    d.features = s.features;
    d.combos = s.combos;
    d.shifts.forEach((x: any, i: number) => {
      x.tablePolicy = s.policies[i] ?? [];
    });
    this.selected = new Set([...this.selected].filter((id) => this.item(id)));
    if (this.combo && !this.doc.combos.find((c: any) => c.id === this.combo)) this.combo = null;
  }
  private commit(before: string) {
    if (before === this.snapshot()) return;
    this.undo.push(before);
    if (this.undo.length > 80) this.undo.shift();
    this.redo = [];
    this.host.changed();
  }
  private plan() {
    const plans = this.doc.floorPlans;
    if (!plans.find((p: any) => p.id === this.planId)) this.planId = plans[0]?.id ?? '';
    return plans.find((p: any) => p.id === this.planId);
  }
  private toItem(r: any, type: 'table' | 'feature'): Item {
    const b = type === 'table' ? tableBox(r) : { w: r.w, h: r.h };
    return { id: r.id, type, ref: r, x: r.x, y: r.y, w: b.w, h: b.h, rotation: r.rotation ?? 0 };
  }
  private items(): Item[] {
    return [
      ...this.features().filter((f: any) => f.floorPlanId === this.planId).map((f: any) => this.toItem(f, 'feature')),
      ...this.doc.tables.filter((t: any) => t.floorPlanId === this.planId).map((t: any) => this.toItem(t, 'table')),
    ];
  }
  private item(id: string): Item | null {
    const t = this.doc.tables.find((x: any) => x.id === id);
    if (t) return this.toItem(t, 'table');
    const f = this.features().find((x: any) => x.id === id);
    return f ? this.toItem(f, 'feature') : null;
  }
  private selectedItems(): Item[] {
    return [...this.selected].map((id) => this.item(id)).filter(Boolean) as Item[];
  }
  private areaColor(areaId: string | null) {
    const i = this.doc.areas.findIndex((a: any) => a.id === areaId);
    return i < 0 ? '#8b95a5' : AREA_COLORS[i % AREA_COLORS.length];
  }
  /** Axis-aligned bounds of an item including its rotation. */
  private bounds(it: Item) {
    const r = (it.rotation * Math.PI) / 180;
    const c = Math.abs(Math.cos(r));
    const s = Math.abs(Math.sin(r));
    const w = it.w * c + it.h * s;
    const h = it.w * s + it.h * c;
    const cx = it.x + it.w / 2;
    const cy = it.y + it.h / 2;
    return { left: cx - w / 2, top: cy - h / 2, right: cx + w / 2, bottom: cy + h / 2 };
  }

  // ------------------------------------------------------------ rendering
  mount(el: HTMLElement) {
    this.el = el;
    el.onpointerdown = (e) => this.onDown(e);
    el.onpointermove = (e) => this.onMove(e);
    el.onpointerup = (e) => this.onUp(e);
    el.onpointercancel = (e) => this.onUp(e);
    el.onclick = (e) => this.onClick(e);
    el.onkeydown = (e) => this.onKey(e);
    el.onchange = (e) => this.onField(e);
    el.oninput = (e) => this.onLabelInput(e);
    el.onwheel = (e) => this.onWheel(e);
    el.ondragstart = (e) => {
      const tile = (e.target as HTMLElement).closest('[data-el]') as HTMLElement | null;
      if (!tile) return;
      e.dataTransfer?.setData('text/plain', `element:${tile.dataset.el}`);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'copy';
    };
    el.ondragover = (e) => {
      if ((e.target as HTMLElement).closest('.lay-canvas')) {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      }
    };
    el.ondrop = (e) => {
      const data = e.dataTransfer?.getData('text/plain') ?? '';
      if (!data.startsWith('element:') || !(e.target as HTMLElement).closest('.lay-canvas')) return;
      e.preventDefault();
      const c = this.canvas()!.getBoundingClientRect();
      this.addElement(data.slice(8), { x: (e.clientX - c.left) / this.zoom, y: (e.clientY - c.top) / this.zoom });
    };
    this.render();
  }

  /** Combinations whose tables are all on the current plan. */
  private planCombos() {
    const onPlan = new Set(this.doc.tables.filter((t: any) => t.floorPlanId === this.planId).map((t: any) => t.id));
    return this.doc.combos.filter((c: any) => c.tableIds.length >= 2 && c.tableIds.every((id: string) => onPlan.has(id)));
  }

  /** Dashed links between the tables of each combination, plus a badge showing its party range. */
  private comboLayer(W: number, H: number): string {
    const combos = this.planCombos();
    if (!combos.length) return '';
    const lines: string[] = [];
    const badges: string[] = [];
    for (const c of combos) {
      const pts = c.tableIds
        .map((id: string) => this.item(id))
        .filter(Boolean)
        .map((it: Item) => ({ x: it.x + it.w / 2, y: it.y + it.h / 2 }))
        .sort((a: any, b: any) => a.x - b.x || a.y - b.y);
      if (pts.length < 2) continue;
      const on = c.id === this.combo;
      lines.push(
        `<polyline class="lay-link ${on ? 'on' : ''}" fill="none" points="${pts.map((p: any) => `${Math.round(p.x)},${Math.round(p.y)}`).join(' ')}"/>`,
      );
      const cx = pts.reduce((sum: number, p: any) => sum + p.x, 0) / pts.length;
      const cy = pts.reduce((sum: number, p: any) => sum + p.y, 0) / pts.length;
      badges.push(
        `<button class="lay-combo ${on ? 'on' : ''}" data-combo="${c.id}" style="left:${Math.round(cx)}px;top:${Math.round(cy)}px"
          title="${esc(T('l.combo.title', { min: c.minParty, max: c.maxParty }))}">⛓ ${c.minParty}–${c.maxParty}</button>`,
      );
    }
    return `<svg class="lay-links" width="${W}" height="${H}" aria-hidden="true">${lines.join('')}</svg>${badges.join('')}`;
  }

  private paintCombos() {
    const layer = this.el?.querySelector('.lay-combo-layer') as HTMLElement | null;
    if (!layer) return;
    const { W, H } = this.canvasSize();
    layer.innerHTML = this.comboLayer(W, H);
  }

  private canvasSize() {
    const items = this.items();
    const right = Math.max(0, ...items.map((i) => this.bounds(i).right));
    const bottom = Math.max(0, ...items.map((i) => this.bounds(i).bottom));
    return { W: Math.max(960, right + 160), H: Math.max(560, bottom + 160) };
  }

  render() {
    if (!this.el) return;
    const plan = this.plan();
    const items = this.items();
    const tables = items.filter((i) => i.type === 'table').map((i) => ({ ...i.ref }));
    const bad = new Set(overlaps(tables).flat());
    const { W, H } = this.canvasSize();
    const single = this.selected.size === 1 ? this.item([...this.selected][0]) : null;

    const comboMembers = new Set<string>(
      (this.doc.combos.find((c: any) => c.id === this.combo)?.tableIds as string[] | undefined) ?? [],
    );
    const planOptions = this.doc.floorPlans
      .map((p: any) => `<option value="${p.id}" ${p.id === this.planId ? 'selected' : ''}>${esc(p.name)}</option>`)
      .join('');

    const boxes = items
      .map((it) => {
        const sel = this.selected.has(it.id);
        const rot = it.rotation ? `transform:rotate(${it.rotation}deg);` : '';
        const common = `left:${it.x}px;top:${it.y}px;width:${it.w}px;height:${it.h}px;${rot}`;
        if (it.type === 'feature') {
          const f = it.ref;
          return `<div class="lay-f f-${f.kind} ${sel ? 'sel' : ''}" data-id="${it.id}" role="button" tabindex="-1"
              aria-label="${esc(featureLabel(f.kind as FeatureKind))}" style="${common}">
              ${f.kind === 'label' ? `<span class="ft">${esc(f.text ?? T('l.feature.label'))}</span>` : ''}
              ${f.kind === 'door' ? '<i class="swing"></i>' : ''}
            </div>`;
        }
        const t = it.ref;
        const counter = it.rotation ? `style="transform:rotate(${-it.rotation}deg)"` : '';
        const member = comboMembers.has(it.id);
        const chairs = chairsFor(t.shape, it.w, it.h, t.maxSeats)
          .map((c) => `<i class="chair" style="left:${Math.round(it.w / 2 + c.x - 11)}px;top:${Math.round(it.h / 2 + c.y - 4)}px;transform:rotate(${Math.round(c.rot)}deg)"></i>`)
          .join('');
        return `<div class="lay-t ${t.shape === 'round' ? 'round' : ''} ${t.shape === 'diamond' ? 'diamond' : ''} ${sel ? 'sel' : ''} ${bad.has(it.id) ? 'bad' : ''} ${member ? 'member' : ''}"
            data-id="${it.id}" tabindex="-1" role="button" aria-label="${esc(T('l.table_aria', { label: t.label, min: t.minSeats, max: t.maxSeats }))}"
            style="${common}">
          ${chairs}
          <i class="adot" style="background:${this.areaColor(t.diningAreaId)}" aria-hidden="true"></i>
          <span class="txt" ${counter}><span class="lab">${esc(t.label)}</span></span>
        </div>`;
      })
      .join('');

    // Handles for a single selection, drawn beside the item so rotation handles stay upright.
    let handles = '';
    if (single && this.tool === 'select') {
      const cx = single.x + single.w / 2;
      const cy = single.y + single.h / 2;
      const rad = (single.rotation * Math.PI) / 180;
      const rot = (px: number, py: number) => ({
        x: cx + px * Math.cos(rad) - py * Math.sin(rad),
        y: cy + px * Math.sin(rad) + py * Math.cos(rad),
      });
      const corner = rot(single.w / 2, single.h / 2);
      const top = rot(0, -single.h / 2 - 22);
      handles =
        `<span class="lay-h" data-handle="resize" style="left:${corner.x - 10}px;top:${corner.y - 10}px" aria-hidden="true"></span>` +
        `<span class="lay-r" data-handle="rotate" style="left:${top.x - 9}px;top:${top.y - 9}px" title="${esc(T('l.rotate_title'))}" aria-hidden="true"></span>`;
    }

    const pct = Math.round(this.zoom * 100);
    const icon = (name: string) => ICONS[name] ?? '';
    const tb = (act: string, label: string, ico: string, extra = '', pressed?: boolean) =>
      `<button class="fp-tb" data-l="${act}" ${extra} title="${esc(label)}" aria-label="${esc(label)}" ${pressed === undefined ? '' : `aria-pressed="${pressed}"`}>${icon(ico)}</button>`;

    // Elements panel: tables first, then the room's own parts.
    const tiles = ELEMENTS.map((el) => {
      const title = TN('l.fp.el', el.seats, { shape: T(`l.shape.${el.shape}`) });
      return `<button class="fp-tile" draggable="true" data-l="el" data-el="${el.id}" title="${esc(title)}" aria-label="${esc(title)}">${elementIcon(el)}</button>`;
    }).join('');
    const roomTiles = (['wall', 'door', 'window', 'pillar', 'label'] as FeatureKind[])
      .map((k) => {
        const l = featureLabel(k);
        const attrs = k === 'wall' ? 'data-l="tool-wall"' : `data-l="add-feature" data-kind="${k}"`;
        return `<button class="fp-tile fp-room" ${attrs} title="${esc(l)}" aria-label="${esc(l)}" ${k === 'wall' ? `aria-pressed="${this.tool === 'wall'}"` : ''}>${icon(`f-${k}`)}<span>${esc(l)}</span></button>`;
      })
      .join('');

    // Right panel: tables, dining areas, floor plans.
    const totalTables = this.doc.tables.length;
    const areaRows = this.doc.areas
      .map((a: any) => {
        const mine = this.doc.tables.filter((t: any) => t.diningAreaId === a.id);
        const plans = new Set(mine.map((t: any) => t.floorPlanId)).size;
        return `<button class="fp-row" data-l="area-select" data-id="${a.id}">
          <i class="adot" style="background:${this.areaColor(a.id)}"></i>
          <span><strong>${esc(a.name)}</strong><br><span class="muted small">${esc(TN('l.fp.tables_n', mine.length))} · ${esc(TN('l.fp.plans_n', plans))}</span></span></button>`;
      })
      .join('');
    const planRows = this.doc.floorPlans
      .map((p: any) => {
        const n = this.doc.tables.filter((t: any) => t.floorPlanId === p.id).length;
        return `<button class="fp-row ${p.id === this.planId ? 'on' : ''}" data-l="plan" data-id="${p.id}">
          <span><strong>${esc(p.name)}</strong><br><span class="muted small">${esc(TN('l.fp.tables_n', n))}</span></span></button>`;
      })
      .join('');

    const oldScroll = this.el.querySelector('.lay-scroll') as HTMLElement | null;
    const keepTop = oldScroll?.scrollTop ?? 0;
    const keepLeft = oldScroll?.scrollLeft ?? 0;

    this.el.innerHTML = `
    <div class="fp">
      <div class="fp-head">
        <h2>${esc(T('l.fp.title'))}</h2>
        <div class="fp-plan">
          <select data-lplan aria-label="${esc(T('l.fp.plan_aria'))}">${planOptions}<option value="__new">${esc(T('l.fp.new_plan'))}</option></select>
          <button class="fp-text" data-l="rename-plan" ${plan ? '' : 'disabled'}>${esc(T('l.rename'))}</button>
          <button class="fp-text" data-l="dup-plan" ${plan ? '' : 'disabled'} title="${esc(T('l.dup_plan_title'))}">${esc(T('l.dup_plan'))}</button>
          <button class="fp-text" data-l="del-plan" ${this.doc.floorPlans.length > 1 && !items.length ? '' : 'disabled'}
            title="${esc(T('l.del_plan_title'))}">${esc(T('l.del_plan'))}</button>
        </div>
      </div>
      <div class="fp-grid">
        <aside class="fp-elements" aria-label="${esc(T('l.fp.elements'))}">
          <h3>${esc(T('l.fp.elements'))}</h3>
          <div class="fp-tiles">${tiles}</div>
          <h3>${esc(T('l.fp.room'))}</h3>
          <div class="fp-tiles room">${roomTiles}</div>
          <p class="muted small">${esc(T('l.fp.elements_hint'))}</p>
        </aside>
        <section class="fp-center">
          <div class="fp-toolbar" role="toolbar" aria-label="${esc(T('l.fp.toolbar'))}">
            <span class="fp-group">${tb('undo', T('l.undo'), 'undo', this.undo.length ? '' : 'disabled')}${tb('redo', T('l.redo'), 'redo', this.redo.length ? '' : 'disabled')}</span>
            <span class="fp-group">${tb('zoom-in', T('l.zoom_in'), 'zoom-in')}${tb('zoom-out', T('l.fp.zoom_out'), 'zoom-out')}${tb('zoom-fit', T('l.fp.zoom_fit'), 'fit')}
              <span class="fp-pct" aria-live="polite">${pct}%</span></span>
            <span class="fp-group">${tb('tool-select', T('l.fp.tool_select'), 'select', '', this.tool === 'select')}${tb('tool-pan', T('l.fp.tool_pan'), 'pan', '', this.tool === 'pan')}</span>
            <span class="fp-group">${tb('rotate', T('l.fp.rotate_left'), 'rot-left', `data-d="-15" ${this.selected.size ? '' : 'disabled'}`)}${tb('rotate', T('l.fp.rotate_right'), 'rot-right', `data-d="15" ${this.selected.size ? '' : 'disabled'}`)}</span>
            <label class="fp-snap"><input type="checkbox" data-l="snap" ${this.snapOn ? 'checked' : ''}> ${esc(T('l.snap'))}</label>
          </div>
          <div class="lay-scroll ${this.tool === 'wall' ? 'drawing' : ''} ${this.tool === 'pan' ? 'panning' : ''}">
            <div class="lay-sizer" style="width:${W * this.zoom}px;height:${H * this.zoom}px">
              <div class="lay-canvas" tabindex="0" aria-label="${esc(T('l.canvas_aria'))}"
                style="width:${W}px;height:${H}px;transform:scale(${this.zoom});transform-origin:0 0">
                <div class="lay-combo-layer">${this.comboLayer(W, H)}</div>${boxes}${handles}<div class="lay-marquee" hidden></div>
              </div>
            </div>
          </div>
          <p class="muted small">${esc(T('l.help'))}
            ${bad.size ? `<span class="cfg-warn"> ${esc(TN('l.overlap', bad.size))}</span>` : ''}</p>
        </section>
        <aside class="fp-right">
          <section class="fp-card">
            <header><h3>${esc(T('l.fp.tables'))}</h3>
              <span class="fp-count">${totalTables}
                <button class="fp-tb" data-l="zoom-fit" title="${esc(T('l.fp.locate'))}" aria-label="${esc(T('l.fp.locate'))}">${icon('locate')}</button></span></header>
            ${this.selected.size || this.combo ? this.inspector(single) : `<p class="muted">${esc(T('l.fp.start'))}</p>`}
          </section>
          <section class="fp-card">
            <header><h3>${esc(T('l.fp.areas'))}</h3><button class="fp-text" data-l="new-area">${esc(T('l.fp.new'))}</button></header>
            ${areaRows || `<p class="muted small">${esc(T('l.fp.no_areas'))}</p>`}
          </section>
          <section class="fp-card">
            <header><h3>${esc(T('l.fp.plans'))}</h3><button class="fp-text" data-l="add-plan">${esc(T('l.fp.new'))}</button></header>
            ${planRows}
          </section>
        </aside>
      </div>
    </div>`;

    const sc = this.el.querySelector('.lay-scroll') as HTMLElement | null;
    if (sc) {
      sc.scrollTop = keepTop;
      sc.scrollLeft = keepLeft;
    }
  }

  private inspector(single: Item | null): string {
    const n = this.selected.size;
    if (!n && this.combo) return this.comboInspector();
    if (!n)
      return `<aside class="lay-insp"><p class="muted small">${esc(T('l.insp_empty'))}</p></aside>`;
    const num = (f: string, v: number, min = 0) => `<input type="number" data-f="${f}" value="${Math.round(v)}" min="${min}" step="1">`;

    if (n > 1) {
      const allTables = this.selectedItems().every((i) => i.type === 'table');
      const A: Align[] = ['left', 'hcenter', 'right', 'top', 'vcenter', 'bottom'];
      return `<aside class="lay-insp">
        <h3>${esc(T('l.selected', { n }))}</h3>
        <span class="muted small">${esc(T('l.align'))}</span>
        <div class="cfg-wrap" style="gap:4px">${A.map((k) => `<button data-l="align" data-a="${k}">${esc(T(`l.align.${k}`))}</button>`).join('')}</div>
        <span class="muted small">${esc(T('l.space'))}</span>
        <div class="cfg-wrap" style="gap:4px">
          <button data-l="distribute" data-a="h" ${n < 3 ? 'disabled' : ''}>${esc(T('l.space_h'))}</button>
          <button data-l="distribute" data-a="v" ${n < 3 ? 'disabled' : ''}>${esc(T('l.space_v'))}</button>
        </div>
        <span class="muted small">${esc(T('l.rotate'))}</span>
        <div class="cfg-wrap" style="gap:4px">
          <button data-l="rotate" data-d="-15">−15°</button><button data-l="rotate" data-d="15">+15°</button>
          <button data-l="rotate" data-d="90">+90°</button>
        </div>
        ${allTables ? this.bulkMove() : ''}
        ${allTables ? `<button class="primary" data-l="combine" title="${esc(T('l.combine_title'))}">${esc(T('l.combine'))}</button>` : ''}
        <div class="cfg-row"><button data-l="dup">${esc(T('l.duplicate'))}</button><button class="danger" data-l="del">${esc(T('l.delete'))}</button></div>
      </aside>`;
    }

    if (!single) return '';
    const rotRow = `<div class="cfg-row">
        <label>${esc(T('l.rotation'))} ${num('rotation', single.rotation)}</label>
        <button data-l="rotate" data-d="-15" aria-label="${esc(T('l.rotate_left'))}">−15°</button>
        <button data-l="rotate" data-d="15" aria-label="${esc(T('l.rotate_right'))}">+15°</button>
      </div>`;
    const posRow = `<div class="cfg-row"><label>X ${num('x', single.x)}</label><label>Y ${num('y', single.y)}</label></div>`;

    if (single.type === 'feature') {
      const f = single.ref;
      return `<aside class="lay-insp">
        <h3>${esc(featureLabel(f.kind as FeatureKind))}</h3>
        ${f.kind === 'label' ? `<label>${esc(T('l.text'))} <input type="text" data-f="text" value="${esc(f.text ?? '')}" maxlength="60"></label>` : ''}
        <div class="cfg-row"><label>${esc(T('l.length'))} ${num('w', f.w, 4)}</label><label>${esc(T('l.thickness'))} ${num('h', f.h, 4)}</label></div>
        ${rotRow}${posRow}
        <div class="cfg-row"><button data-l="dup">${esc(T('l.duplicate'))}</button><button class="danger" data-l="del">${esc(T('l.delete'))}</button></div>
      </aside>`;
    }

    const t = single.ref;
    const inCombos = this.doc.combos.filter((c: any) => c.tableIds.includes(t.id));
    const comboChips = inCombos.length
      ? `<span class="muted small">${esc(T('l.in_combos'))}</span><div class="cfg-wrap" style="gap:4px">${inCombos
          .map((c: any) => `<button data-l="combo-select" data-id="${c.id}">⛓ ${c.minParty}–${c.maxParty}</button>`)
          .join('')}</div>`
      : '';
    const areas = [`<option value="">${esc(T('l.area_none'))}</option>`]
      .concat(this.doc.areas.map((a: any) => `<option value="${a.id}" ${a.id === t.diningAreaId ? 'selected' : ''}>${esc(a.name)}</option>`))
      .join('');
    return `<aside class="lay-insp">
      <h3>${esc(T('l.table', { label: t.label }))}</h3>
      <label>${esc(T('l.label'))} <input type="text" data-f="label" value="${esc(t.label)}" maxlength="20"></label>
      <div class="cfg-row"><label>${esc(T('l.min_seats'))} ${num('minSeats', t.minSeats, 1)}</label><label>${esc(T('l.max_seats'))} ${num('maxSeats', t.maxSeats, 1)}</label></div>
      <label>${esc(T('l.area'))} <select data-f="diningAreaId">${areas}</select></label>
      ${this.doc.floorPlans.length > 1 ? this.bulkMove(true) : ''}
      <label>${esc(T('l.shape'))} <select data-f="shape">${['rect', 'round', 'diamond'].map((s) => `<option value="${s}" ${t.shape === s ? 'selected' : ''}>${esc(T(`l.shape.${s}`))}</option>`).join('')}</select></label>
      <div class="cfg-row"><label>${esc(T('l.width'))} ${num('w', single.w, MIN_SIZE)}</label><label>${esc(T('l.height'))} ${num('h', single.h, MIN_SIZE)}</label></div>
      ${rotRow}${posRow}
      <div class="cfg-row">
        <button data-l="auto-size" ${t.w > 1 ? '' : 'disabled'} title="${esc(T('l.auto_size_title'))}">${esc(T('l.auto_size'))}</button>
        <button data-l="dup">${esc(T('l.duplicate'))}</button>
        <button class="danger" data-l="del">${esc(T('l.delete'))}</button>
      </div>
      ${comboChips}
    </aside>`;
  }

  private comboInspector(): string {
    const c = this.doc.combos.find((x: any) => x.id === this.combo);
    if (!c) return '';
    const members = c.tableIds
      .map((id: string) => this.doc.tables.find((t: any) => t.id === id))
      .filter(Boolean)
      .map(
        (t: any) =>
          `<span class="chip">${esc(t.label)} <button class="ghost" data-l="combo-remove" data-id="${t.id}" aria-label="${esc(T('l.c.remove_aria', { label: t.label }))}" ${c.tableIds.length <= 2 ? 'disabled' : ''}>×</button></span>`,
      )
      .join('');
    const plan = this.doc.tables.filter((t: any) => t.floorPlanId === this.planId && !c.tableIds.includes(t.id));
    const seats = c.tableIds.reduce((n: number, id: string) => n + (this.doc.tables.find((t: any) => t.id === id)?.maxSeats ?? 0), 0);
    return `<aside class="lay-insp">
      <h3>${esc(T('l.c.title'))}</h3>
      <p class="muted small">${esc(T('l.c.hint', { n: seats }))}</p>
      <div class="cfg-row">
        <label>${esc(T('l.c.from'))} <input type="number" data-c="minParty" value="${c.minParty}" min="1"></label>
        <label>${esc(T('l.c.to'))} <input type="number" data-c="maxParty" value="${c.maxParty}" min="1"></label>
      </div>
      <span class="muted small">${esc(T('l.c.tables'))}</span>
      <div class="cfg-wrap" style="gap:4px">${members}</div>
      <label>${esc(T('l.c.add'))} <select data-c="add"><option value="">${esc(T('l.c.choose'))}</option>${plan
        .map((t: any) => `<option value="${t.id}">${esc(t.label)} (${t.minSeats}-${t.maxSeats})</option>`)
        .join('')}</select></label>
      <div class="cfg-row"><button class="danger" data-l="combo-del">${esc(T('l.c.delete'))}</button></div>
    </aside>`;
  }

  // ------------------------------------------------------------ pointer handling
  private canvas() {
    return this.el?.querySelector('.lay-canvas') as HTMLElement | null;
  }
  /** Pointer position in canvas coordinates (undoing scroll and zoom). */
  private at(e: PointerEvent) {
    const r = this.canvas()!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / this.zoom, y: (e.clientY - r.top) / this.zoom };
  }

  private onDown(e: PointerEvent) {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    const canvas = target.closest('.lay-canvas') as HTMLElement | null;
    if (!canvas) return;
    const p = this.at(e);
    const multi = e.shiftKey || e.ctrlKey || e.metaKey;

    if (this.tool === 'pan') {
      const sc = this.el!.querySelector('.lay-scroll') as HTMLElement;
      this.drag = {
        kind: 'pan', px: e.clientX, py: e.clientY, before: this.snapshot(), moved: false, origins: [],
        sx: sc.scrollLeft, sy: sc.scrollTop,
      };
      canvas.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    if (this.tool === 'wall') {
      this.drag = {
        kind: 'wall', px: e.clientX, py: e.clientY, before: this.snapshot(), moved: false, origins: [],
        sx: snap(p.x, this.snapOn), sy: snap(p.y, this.snapOn),
      };
      canvas.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    const badge = target.closest('[data-combo]') as HTMLElement | null;
    if (badge) {
      this.combo = badge.dataset.combo!;
      this.selected.clear();
      this.render();
      e.preventDefault();
      return;
    }

    const handle = target.closest('[data-handle]') as HTMLElement | null;
    if (handle && this.selected.size === 1) {
      const it = this.item([...this.selected][0])!;
      this.drag = {
        kind: handle.dataset.handle === 'rotate' ? 'rotate' : 'resize',
        px: e.clientX, py: e.clientY, before: this.snapshot(), moved: false, origins: [],
        id: it.id, ow: it.w, oh: it.h, ox: it.x, oy: it.y,
      };
      canvas.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    const box = target.closest('.lay-t, .lay-f') as HTMLElement | null;
    if (box) {
      const id = box.dataset.id!;
      this.combo = null;
      if (multi) {
        if (this.selected.has(id)) this.selected.delete(id);
        else this.selected.add(id);
        this.render();
        e.preventDefault();
        return;
      }
      if (!this.selected.has(id)) {
        this.selected = new Set([id]);
        this.render();
      }
      this.drag = {
        kind: 'move', px: e.clientX, py: e.clientY, before: this.snapshot(), moved: false, leader: id,
        origins: this.selectedItems().map((i) => ({ id: i.id, x: i.x, y: i.y })),
      };
      this.canvas()!.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    // Empty canvas: marquee selection.
    this.drag = {
      kind: 'marquee', px: e.clientX, py: e.clientY, before: this.snapshot(), moved: false, origins: [],
      sx: p.x, sy: p.y, additive: multi,
    };
    if (!multi && (this.selected.size || this.combo)) {
      this.selected.clear();
      this.combo = null;
      this.render();
    }
    this.canvas()!.setPointerCapture?.(e.pointerId);
    this.canvas()!.focus();
    e.preventDefault();
  }

  private setBox(id: string) {
    const it = this.item(id);
    const node = this.el?.querySelector(`[data-id="${id}"]`) as HTMLElement | null;
    if (!it || !node) return;
    node.style.left = `${it.x}px`;
    node.style.top = `${it.y}px`;
    node.style.width = `${it.w}px`;
    node.style.height = `${it.h}px`;
    node.style.transform = it.rotation ? `rotate(${it.rotation}deg)` : '';
    const txt = node.querySelector('.txt') as HTMLElement | null;
    if (txt) txt.style.transform = it.rotation ? `rotate(${-it.rotation}deg)` : '';
  }

  private onMove(e: PointerEvent) {
    const d = this.drag;
    if (!d) return;
    if (d.kind === 'pan') {
      const sc = this.el!.querySelector('.lay-scroll') as HTMLElement;
      sc.scrollLeft = d.sx! - (e.clientX - d.px);
      sc.scrollTop = d.sy! - (e.clientY - d.py);
      d.moved = true;
      return;
    }
    const dx = (e.clientX - d.px) / this.zoom;
    const dy = (e.clientY - d.py) / this.zoom;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 3 / this.zoom) return;
    d.moved = true;
    const on = this.snapOn && !e.altKey;

    if (d.kind === 'move') {
      const lead = d.origins.find((o) => o.id === d.leader) ?? d.origins[0];
      let sx = snap(lead.x + dx, on) - lead.x;
      let sy = snap(lead.y + dy, on) - lead.y;
      const minX = Math.min(...d.origins.map((o) => o.x));
      const minY = Math.min(...d.origins.map((o) => o.y));
      if (minX + sx < 0) sx = -minX;
      if (minY + sy < 0) sy = -minY;
      for (const o of d.origins) {
        const r = this.item(o.id)?.ref;
        if (!r) continue;
        r.x = Math.min(MAX, o.x + sx);
        r.y = Math.min(MAX, o.y + sy);
        this.setBox(o.id);
      }
      this.moveHandles();
      this.paintCombos();
    } else if (d.kind === 'resize') {
      const it = this.item(d.id!)!;
      const r = it.ref;
      const th = (it.rotation * Math.PI) / 180;
      // Pointer movement in the item's own (rotated) frame.
      const lx = dx * Math.cos(th) + dy * Math.sin(th);
      const ly = -dx * Math.sin(th) + dy * Math.cos(th);
      const isTable = it.type === 'table';
      const min = isTable ? MIN_SIZE : 4;
      const max = isTable ? 600 : 2000;
      const nw = Math.max(min, Math.min(max, snap(d.ow! + lx, on)));
      const nh = Math.max(min, Math.min(max, snap(d.oh! + ly, on)));
      // Keep the top-left corner fixed on screen: the centre moves by half the growth, rotated.
      const gx = (nw - d.ow!) / 2;
      const gy = (nh - d.oh!) / 2;
      const cx = d.ox! + d.ow! / 2 + gx * Math.cos(th) - gy * Math.sin(th);
      const cy = d.oy! + d.oh! / 2 + gx * Math.sin(th) + gy * Math.cos(th);
      r.w = nw;
      r.h = nh;
      r.x = Math.max(0, Math.round(cx - nw / 2));
      r.y = Math.max(0, Math.round(cy - nh / 2));
      this.setBox(d.id!);
      this.moveHandles();
      this.paintCombos();
    } else if (d.kind === 'rotate') {
      const it = this.item(d.id!)!;
      const p = this.at(e);
      const cx = it.x + it.w / 2;
      const cy = it.y + it.h / 2;
      const ang = (Math.atan2(p.y - cy, p.x - cx) * 180) / Math.PI + 90;
      it.ref.rotation = norm(e.altKey ? ang : Math.round(ang / 15) * 15);
      this.setBox(d.id!);
      this.moveHandles();
    } else if (d.kind === 'marquee') {
      const p = this.at(e);
      const m = this.el!.querySelector('.lay-marquee') as HTMLElement;
      m.hidden = false;
      m.className = 'lay-marquee';
      m.style.transform = '';
      m.style.left = `${Math.min(d.sx!, p.x)}px`;
      m.style.top = `${Math.min(d.sy!, p.y)}px`;
      m.style.width = `${Math.abs(p.x - d.sx!)}px`;
      m.style.height = `${Math.abs(p.y - d.sy!)}px`;
    } else if (d.kind === 'wall') {
      const p = this.at(e);
      let ex = snap(p.x, on);
      let ey = snap(p.y, on);
      if (on) {
        const ang = Math.abs((Math.atan2(ey - d.sy!, ex - d.sx!) * 180) / Math.PI);
        if (ang < 7 || ang > 173) ey = d.sy!;
        else if (Math.abs(ang - 90) < 7) ex = d.sx!;
      }
      d.ex = ex;
      d.ey = ey;
      const m = this.el!.querySelector('.lay-marquee') as HTMLElement;
      m.hidden = false;
      m.className = 'lay-marquee wall';
      m.style.left = `${d.sx}px`;
      m.style.top = `${d.sy! - 5}px`;
      m.style.width = `${Math.hypot(ex - d.sx!, ey - d.sy!)}px`;
      m.style.height = '10px';
      m.style.transformOrigin = '0 50%';
      m.style.transform = `rotate(${(Math.atan2(ey - d.sy!, ex - d.sx!) * 180) / Math.PI}deg)`;
    }
    this.growCanvas();
  }

  /** Re-position the resize/rotate handles while an item is being changed. */
  private moveHandles() {
    if (this.selected.size !== 1) return;
    const it = this.item([...this.selected][0]);
    if (!it) return;
    const cx = it.x + it.w / 2;
    const cy = it.y + it.h / 2;
    const rad = (it.rotation * Math.PI) / 180;
    const rot = (px: number, py: number) => ({
      x: cx + px * Math.cos(rad) - py * Math.sin(rad),
      y: cy + px * Math.sin(rad) + py * Math.cos(rad),
    });
    const c = rot(it.w / 2, it.h / 2);
    const t = rot(0, -it.h / 2 - 22);
    const h = this.el!.querySelector('.lay-h') as HTMLElement | null;
    const r = this.el!.querySelector('.lay-r') as HTMLElement | null;
    if (h) {
      h.style.left = `${c.x - 10}px`;
      h.style.top = `${c.y - 10}px`;
    }
    if (r) {
      r.style.left = `${t.x - 9}px`;
      r.style.top = `${t.y - 9}px`;
    }
  }

  private growCanvas() {
    const c = this.canvas();
    const sizer = this.el?.querySelector('.lay-sizer') as HTMLElement | null;
    if (!c || !sizer) return;
    const { W, H } = this.canvasSize();
    c.style.width = `${W}px`;
    c.style.height = `${H}px`;
    sizer.style.width = `${W * this.zoom}px`;
    sizer.style.height = `${H * this.zoom}px`;
  }

  private onUp(e: PointerEvent) {
    const d = this.drag;
    if (!d) return;
    this.drag = null;
    this.canvas()?.releasePointerCapture?.(e.pointerId);

    if (d.kind === 'pan') return;

    if (d.kind === 'marquee') {
      if (d.moved) {
        const p = this.at(e);
        const x1 = Math.min(d.sx!, p.x);
        const x2 = Math.max(d.sx!, p.x);
        const y1 = Math.min(d.sy!, p.y);
        const y2 = Math.max(d.sy!, p.y);
        const next = d.additive ? new Set(this.selected) : new Set<string>();
        for (const it of this.items()) {
          const b = this.bounds(it);
          if (b.left < x2 && b.right > x1 && b.top < y2 && b.bottom > y1) next.add(it.id);
        }
        this.selected = next;
      }
      this.render();
      return;
    }

    if (d.kind === 'wall') {
      this.tool = 'select';
      if (d.moved && d.ex !== undefined && d.ey !== undefined) {
        const len = Math.round(Math.hypot(d.ex - d.sx!, d.ey - d.sy!));
        if (len >= 24) {
          const rot = norm((Math.atan2(d.ey - d.sy!, d.ex - d.sx!) * 180) / Math.PI);
          const cx = (d.sx! + d.ex) / 2;
          const cy = (d.sy! + d.ey) / 2;
          const f = {
            id: uid(), floorPlanId: this.planId, kind: 'wall', w: len, h: 10, rotation: rot,
            x: Math.max(0, Math.round(cx - len / 2)), y: Math.max(0, Math.round(cy - 5)), text: null,
          };
          this.features().push(f);
          this.selected = new Set([f.id]);
          this.commit(d.before);
        }
      }
      this.render();
      return;
    }

    if (d.moved) {
      this.commit(d.before);
      this.render();
    }
  }

  // ------------------------------------------------------------ keyboard & wheel
  private onWheel(e: WheelEvent) {
    if (!(e.ctrlKey || e.metaKey) || !(e.target as HTMLElement).closest('.lay-scroll')) return;
    e.preventDefault();
    this.setZoom(e.deltaY < 0 ? 1 : -1);
  }

  private setZoom(dir: number | 'fit') {
    if (dir === 'fit') {
      const sc = this.el?.querySelector('.lay-scroll') as HTMLElement | null;
      const its = this.items();
      if (sc && its.length) {
        const bs = its.map((i) => this.bounds(i));
        const w = Math.max(...bs.map((b) => b.right)) + 40;
        const h = Math.max(...bs.map((b) => b.bottom)) + 40;
        this.zoom = Math.max(0.3, Math.min(1.5, Math.floor(Math.min(sc.clientWidth / w, sc.clientHeight / h) * 20) / 20));
      } else this.zoom = 1;
    }
    else {
      // Step to the next stop above or below the current zoom, even after a "fit".
      const next = dir > 0 ? ZOOMS.find((z) => z > this.zoom + 0.01) : [...ZOOMS].reverse().find((z) => z < this.zoom - 0.01);
      this.zoom = next ?? this.zoom;
    }
    this.render();
  }

  private onKey(e: KeyboardEvent) {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    const mod = e.ctrlKey || e.metaKey;
    const k = e.key.toLowerCase();
    if (mod && k === 'z') {
      e.preventDefault();
      return e.shiftKey ? this.doRedo() : this.doUndo();
    }
    if (mod && k === 'y') {
      e.preventDefault();
      return this.doRedo();
    }
    if (mod && k === 'a') {
      e.preventDefault();
      this.selected = new Set(this.items().map((i) => i.id));
      return this.render();
    }
    if (k === 'escape') {
      this.tool = 'select';
      this.selected.clear();
      return this.render();
    }
    if (k === '+' || k === '=') return this.setZoom(1);
    if (k === '-') return this.setZoom(-1);
    if (!this.selected.size && this.combo && (k === 'delete' || k === 'backspace')) {
      e.preventDefault();
      return this.deleteCombo();
    }
    if (!this.selected.size) return;
    const step = e.shiftKey ? SNAP * 4 : SNAP;
    const move: Record<string, [number, number]> = {
      arrowleft: [-step, 0], arrowright: [step, 0], arrowup: [0, -step], arrowdown: [0, step],
    };
    if (move[k]) {
      e.preventDefault();
      const before = this.snapshot();
      const items = this.selectedItems();
      const dx = Math.max(-Math.min(...items.map((i) => i.x)), move[k][0]);
      const dy = Math.max(-Math.min(...items.map((i) => i.y)), move[k][1]);
      items.forEach((i) => {
        i.ref.x += dx;
        i.ref.y += dy;
      });
      this.commit(before);
      this.render();
      this.canvas()?.focus();
    } else if (k === 'delete' || k === 'backspace') {
      e.preventDefault();
      this.removeSelected();
    } else if (k === 'r' && !mod) {
      e.preventDefault();
      this.rotateSelected(e.shiftKey ? -15 : 15);
    } else if (mod && k === 'd') {
      e.preventDefault();
      this.duplicate();
    }
  }

  // ------------------------------------------------------------ edits
  private doUndo() {
    const prev = this.undo.pop();
    if (prev === undefined) return;
    this.redo.push(this.snapshot());
    this.restore(prev);
    this.host.changed();
    this.render();
  }
  private doRedo() {
    const next = this.redo.pop();
    if (next === undefined) return;
    this.undo.push(this.snapshot());
    this.restore(next);
    this.host.changed();
    this.render();
  }

  private freeSpot(w: number, h: number): { x: number; y: number } {
    const placed = this.items().map((i) => this.bounds(i));
    for (let y = SNAP; y < 2400; y += SNAP * 2)
      for (let x = SNAP; x < 1500; x += SNAP * 2) {
        const hit = placed.some((p) => x < p.right + SNAP && p.left < x + w + SNAP && y < p.bottom + SNAP && p.top < y + h + SNAP);
        if (!hit) return { x, y };
      }
    return { x: SNAP, y: SNAP };
  }

  private nextLabelFor(planId: string): string {
    const used = new Set(this.doc.tables.filter((t: any) => t.floorPlanId === planId).map((t: any) => String(t.label).toLowerCase()));
    for (let n = used.size + 1; n < 10_000; n += 1) if (!used.has(String(n))) return String(n);
    return uid().slice(0, 4);
  }

  private addTable() {
    if (!this.planId) return;
    const before = this.snapshot();
    const { w, h } = tableBox({ maxSeats: 4, shape: 'rect', w: 1, h: 1 });
    const at = this.freeSpot(w, h);
    const t = {
      id: uid(), label: this.nextLabelFor(this.planId), floorPlanId: this.planId, diningAreaId: this.doc.areas[0]?.id ?? null,
      minSeats: 2, maxSeats: 4, category: 'regular', shape: 'rect', x: at.x, y: at.y, w: 1, h: 1, rotation: 0,
    };
    this.doc.tables.push(t);
    this.selected = new Set([t.id]);
    this.commit(before);
    this.render();
  }

  /** Add a table from the Elements panel, at a dropped point or the first free spot. */
  private addElement(id: string, at?: { x: number; y: number }) {
    const spec = ELEMENTS.find((e) => e.id === id);
    if (!spec || !this.planId) return;
    const before = this.snapshot();
    const pos = at
      ? { x: Math.max(0, snap(at.x - spec.w / 2, this.snapOn)), y: Math.max(0, snap(at.y - spec.h / 2, this.snapOn)) }
      : this.freeSpot(spec.w, spec.h);
    const t = {
      id: uid(), label: this.nextLabelFor(this.planId), floorPlanId: this.planId, diningAreaId: this.doc.areas[0]?.id ?? null,
      minSeats: Math.max(1, Math.ceil(spec.seats / 2)), maxSeats: spec.seats, category: 'regular', shape: spec.shape,
      x: pos.x, y: pos.y, w: spec.w, h: spec.h, rotation: 0,
    };
    this.doc.tables.push(t);
    this.selected = new Set([t.id]);
    this.combo = null;
    this.commit(before);
    this.render();
  }

  private addFeature(kind: FeatureKind) {
    if (!this.planId) return;
    const before = this.snapshot();
    const size = FEATURE_DEFAULT[kind];
    const at = this.freeSpot(size.w, size.h);
    const f = {
      id: uid(), floorPlanId: this.planId, kind, x: at.x, y: at.y, w: size.w, h: size.h, rotation: 0,
      text: kind === 'label' ? T('l.feature.label') : null,
    };
    this.features().push(f);
    this.selected = new Set([f.id]);
    this.commit(before);
    this.render();
  }

  private duplicate() {
    const src = this.selectedItems();
    if (!src.length) return;
    const before = this.snapshot();
    const next = new Set<string>();
    for (const it of src) {
      const copy = { ...it.ref, id: uid(), x: Math.min(MAX, it.x + SNAP * 2), y: Math.min(MAX, it.y + SNAP * 2) };
      if (it.type === 'table') {
        copy.label = this.nextLabelFor(copy.floorPlanId);
        this.doc.tables.push(copy);
      } else this.features().push(copy);
      next.add(copy.id);
    }
    this.selected = next;
    this.commit(before);
    this.render();
  }

  private combine() {
    const tables = this.selectedItems().filter((i) => i.type === 'table');
    if (tables.length < 2) return;
    const ids = tables.map((t) => t.id).sort();
    const dupe = this.doc.combos.find(
      (c: any) => c.tableIds.length === ids.length && [...c.tableIds].sort().every((x: string, i: number) => x === ids[i]),
    );
    if (dupe) {
      this.combo = dupe.id;
      this.selected.clear();
      this.host.toast(T('l.c.exists'));
      return this.render();
    }
    const before = this.snapshot();
    const seats = tables.map((t) => t.ref.maxSeats as number);
    const total = seats.reduce((a, b) => a + b, 0);
    const c = { id: uid(), minParty: Math.min(total, Math.max(...seats) + 1), maxParty: total, tableIds: ids };
    this.doc.combos.push(c);
    this.combo = c.id;
    this.selected.clear();
    this.commit(before);
    this.render();
  }

  private deleteCombo() {
    if (!this.combo) return;
    const before = this.snapshot();
    this.doc.combos = this.doc.combos.filter((c: any) => c.id !== this.combo);
    this.combo = null;
    this.commit(before);
    this.render();
  }

  private removeSelected() {
    if (!this.selected.size) return;
    const ids = new Set(this.selected);
    const before = this.snapshot();
    const d = this.doc;
    d.tables = d.tables.filter((t: any) => !ids.has(t.id));
    d.features = this.features().filter((f: any) => !ids.has(f.id));
    for (const c of d.combos) c.tableIds = c.tableIds.filter((x: string) => !ids.has(x));
    d.combos = d.combos.filter((c: any) => c.tableIds.length >= 2);
    for (const s of d.shifts) s.tablePolicy = s.tablePolicy.filter((p: any) => !ids.has(p.tableId));
    this.selected.clear();
    this.commit(before);
    this.render();
  }

  private rotateSelected(delta: number) {
    const items = this.selectedItems();
    if (!items.length) return;
    const before = this.snapshot();
    if (items.length === 1) items[0].ref.rotation = norm(items[0].rotation + delta);
    else {
      // Rotate the group about its centre so it keeps its shape.
      const bs = items.map((i) => this.bounds(i));
      const cx = (Math.min(...bs.map((b) => b.left)) + Math.max(...bs.map((b) => b.right))) / 2;
      const cy = (Math.min(...bs.map((b) => b.top)) + Math.max(...bs.map((b) => b.bottom))) / 2;
      const a = (delta * Math.PI) / 180;
      const moved = items.map((it) => {
        const mx = it.x + it.w / 2 - cx;
        const my = it.y + it.h / 2 - cy;
        const ncx = cx + mx * Math.cos(a) - my * Math.sin(a);
        const ncy = cy + mx * Math.sin(a) + my * Math.cos(a);
        return { it, x: ncx - it.w / 2, y: ncy - it.h / 2, rot: norm(it.rotation + delta) };
      });
      // Keep the group's shape: slide it back onto the canvas as one piece rather than clamping each table.
      const nb = moved.map((m) => this.bounds({ ...m.it, x: m.x, y: m.y, rotation: m.rot }));
      const shiftX = Math.max(0, -Math.min(...nb.map((b) => b.left)));
      const shiftY = Math.max(0, -Math.min(...nb.map((b) => b.top)));
      for (const m of moved) {
        m.it.ref.x = Math.round(m.x + shiftX);
        m.it.ref.y = Math.round(m.y + shiftY);
        m.it.ref.rotation = m.rot;
      }
    }
    this.commit(before);
    this.render();
  }

  private align(a: Align) {
    const items = this.selectedItems();
    if (items.length < 2) return;
    const before = this.snapshot();
    const bs = items.map((i) => ({ i, b: this.bounds(i) }));
    const left = Math.min(...bs.map((x) => x.b.left));
    const right = Math.max(...bs.map((x) => x.b.right));
    const top = Math.min(...bs.map((x) => x.b.top));
    const bottom = Math.max(...bs.map((x) => x.b.bottom));
    for (const { i, b } of bs) {
      let dx = 0;
      let dy = 0;
      if (a === 'left') dx = left - b.left;
      else if (a === 'right') dx = right - b.right;
      else if (a === 'hcenter') dx = (left + right) / 2 - (b.left + b.right) / 2;
      else if (a === 'top') dy = top - b.top;
      else if (a === 'bottom') dy = bottom - b.bottom;
      else dy = (top + bottom) / 2 - (b.top + b.bottom) / 2;
      i.ref.x = Math.max(0, Math.round(i.x + dx));
      i.ref.y = Math.max(0, Math.round(i.y + dy));
    }
    this.commit(before);
    this.render();
  }

  private distribute(axis: 'h' | 'v') {
    const items = this.selectedItems();
    if (items.length < 3) return;
    const before = this.snapshot();
    const lo = (b: { left: number; top: number }) => (axis === 'h' ? b.left : b.top);
    const size = (b: { left: number; right: number; top: number; bottom: number }) =>
      axis === 'h' ? b.right - b.left : b.bottom - b.top;
    const bs = items.map((i) => ({ i, b: this.bounds(i) })).sort((p, q) => lo(p.b) - lo(q.b));
    const first = bs[0].b;
    const last = bs[bs.length - 1].b;
    const span = (axis === 'h' ? last.right : last.bottom) - lo(first);
    const gap = (span - bs.reduce((s, x) => s + size(x.b), 0)) / (bs.length - 1);
    let cursor = lo(first);
    for (const { i, b } of bs) {
      const delta = cursor - lo(b);
      if (axis === 'h') i.ref.x = Math.max(0, Math.round(i.x + delta));
      else i.ref.y = Math.max(0, Math.round(i.y + delta));
      cursor += size(b) + gap;
    }
    this.commit(before);
    this.render();
  }

  // ------------------------------------------------------------ clicks and fields
  private onClick(e: MouseEvent) {
    const b = (e.target as HTMLElement).closest('[data-l]') as HTMLElement | null;
    if (!b) return;
    switch (b.dataset.l) {
      case 'plan':
        this.planId = b.dataset.id!;
        this.selected.clear();
        return this.render();
      case 'add-table':
        return this.addTable();
      case 'el':
        return this.addElement(b.dataset.el!);
      case 'tool-select':
        this.tool = 'select';
        return this.render();
      case 'tool-pan':
        this.tool = this.tool === 'pan' ? 'select' : 'pan';
        return this.render();
      case 'area-select': {
        const ids = this.doc.tables.filter((t: any) => t.diningAreaId === b.dataset.id);
        if (!ids.length) return this.host.toast(T('l.fp.area_empty'));
        // Show the plan that has most of the area's tables, then select them.
        const count = new Map<string, number>();
        for (const t of ids) count.set(t.floorPlanId, (count.get(t.floorPlanId) ?? 0) + 1);
        this.planId = [...count.entries()].sort((x, y) => y[1] - x[1])[0][0];
        this.selected = new Set(ids.filter((t: any) => t.floorPlanId === this.planId).map((t: any) => t.id));
        this.combo = null;
        return this.render();
      }
      case 'new-area': {
        const name = window.prompt(T('l.fp.area_prompt'), '')?.trim();
        if (!name) return;
        const before = this.snapshot();
        this.doc.areas.push({ id: uid(), name: name.slice(0, 60), environment: 'indoor', bookable: true, names: {}, descriptions: {} });
        this.commit(before);
        this.host.changed();
        this.host.toast(T('l.fp.area_added'));
        return this.render();
      }
      case 'add-feature':
        return this.addFeature(b.dataset.kind as FeatureKind);
      case 'tool-wall':
        this.tool = this.tool === 'wall' ? 'select' : 'wall';
        this.selected.clear();
        return this.render();
      case 'dup':
        return this.duplicate();
      case 'del':
        return this.removeSelected();
      case 'combine':
        return this.combine();
      case 'combo-select':
        this.combo = b.dataset.id!;
        this.selected.clear();
        return this.render();
      case 'combo-del':
        return this.deleteCombo();
      case 'combo-remove': {
        const c = this.doc.combos.find((x: any) => x.id === this.combo);
        if (!c || c.tableIds.length <= 2) return;
        const before = this.snapshot();
        c.tableIds = c.tableIds.filter((id: string) => id !== b.dataset.id);
        this.commit(before);
        return this.render();
      }
      case 'undo':
        return this.doUndo();
      case 'redo':
        return this.doRedo();
      case 'rotate':
        return this.rotateSelected(Number(b.dataset.d));
      case 'align':
        return this.align(b.dataset.a as Align);
      case 'distribute':
        return this.distribute(b.dataset.a as 'h' | 'v');
      case 'zoom-in':
        return this.setZoom(1);
      case 'zoom-out':
        return this.setZoom(-1);
      case 'zoom-fit':
        return this.setZoom('fit');
      case 'auto-size': {
        const it = this.selectedItems()[0];
        if (!it || it.type !== 'table') return;
        const before = this.snapshot();
        it.ref.w = 1;
        it.ref.h = 1;
        this.commit(before);
        return this.render();
      }
      case 'add-plan': {
        const name = window.prompt(T('l.plan_prompt'), T('l.plan_default', { n: this.doc.floorPlans.length + 1 }))?.trim();
        if (!name) return;
        const before = this.snapshot();
        const p = { id: uid(), name: name.slice(0, 80) };
        this.doc.floorPlans.push(p);
        // A new floor is part of every shift that restricts its plans, so it is usable right away.
        for (const sh of this.doc.shifts) if (sh.floorPlanIds.length) sh.floorPlanIds.push(p.id);
        this.planId = p.id;
        this.selected.clear();
        this.commit(before);
        this.host.changed();
        this.host.toast(T('l.plan_added'));
        return this.render();
      }
      case 'dup-plan': {
        const src = this.plan();
        if (!src) return;
        const name = window.prompt(T('l.plan_prompt'), T('l.dup_plan_name', { name: src.name }))?.trim();
        if (!name) return;
        const before = this.snapshot();
        const p = { id: uid(), name: name.slice(0, 80) };
        this.doc.floorPlans.push(p);
        const idMap = new Map<string, string>();
        for (const t of this.doc.tables.filter((x: any) => x.floorPlanId === src.id)) {
          const copy = { ...t, id: uid(), floorPlanId: p.id };
          idMap.set(t.id, copy.id);
          this.doc.tables.push(copy);
        }
        for (const f of this.features().filter((x: any) => x.floorPlanId === src.id))
          this.features().push({ ...f, id: uid(), floorPlanId: p.id });
        // Combinations whose tables were all copied are copied too.
        for (const c of [...this.doc.combos])
          if (c.tableIds.every((id: string) => idMap.has(id)))
            this.doc.combos.push({ ...c, id: uid(), tableIds: c.tableIds.map((id: string) => idMap.get(id)) });
        for (const s of this.doc.shifts) if (s.floorPlanIds.includes(src.id)) s.floorPlanIds.push(p.id);
        this.planId = p.id;
        this.selected.clear();
        this.commit(before);
        this.host.changed();
        this.host.toast(T('l.plan_added'));
        return this.render();
      }
      case 'rename-plan': {
        const p = this.plan();
        const name = p && window.prompt(T('l.plan_rename'), p.name)?.trim();
        if (!p || !name) return;
        p.name = name.slice(0, 80);
        this.host.changed();
        return this.render();
      }
      case 'del-plan': {
        if (this.items().length || this.doc.floorPlans.length < 2) return;
        const id = this.planId;
        this.doc.floorPlans = this.doc.floorPlans.filter((p: any) => p.id !== id);
        for (const s of this.doc.shifts) s.floorPlanIds = s.floorPlanIds.filter((x: string) => x !== id);
        this.planId = this.doc.floorPlans[0].id;
        this.host.changed();
        return this.render();
      }
      case 'snap':
        this.snapOn = (b as HTMLInputElement).checked;
        return;
    }
  }

  private onLabelInput(e: Event) {
    const el = e.target as HTMLInputElement;
    if ((el.dataset.f !== 'label' && el.dataset.f !== 'text') || this.selected.size !== 1) return;
    const it = this.item([...this.selected][0]);
    if (!it) return;
    if (el.dataset.f === 'label' && it.type === 'table') {
      it.ref.label = el.value;
      const lab = this.el!.querySelector(`.lay-t[data-id="${it.id}"] .lab`);
      if (lab) lab.textContent = el.value;
    } else if (el.dataset.f === 'text' && it.type === 'feature') {
      it.ref.text = el.value.slice(0, 60) || null;
      const ft = this.el!.querySelector(`.lay-f[data-id="${it.id}"] .ft`);
      if (ft) ft.textContent = el.value || 'Label';
    }
    this.host.changed();
  }

  /** Area and floor-plan choices that apply to every selected table at once. */
  private bulkMove(planOnly = false): string {
    const areaOpts = [`<option value="__keep">${esc(T('l.bulk_keep'))}</option>`, `<option value="">${esc(T('l.area_none'))}</option>`]
      .concat(this.doc.areas.map((a: any) => `<option value="${a.id}">${esc(a.name)}</option>`))
      .join('');
    const planOpts = [`<option value="__keep">${esc(T('l.bulk_keep'))}</option>`]
      .concat(this.doc.floorPlans.filter((p: any) => p.id !== this.planId).map((p: any) => `<option value="${p.id}">${esc(p.name)}</option>`))
      .join('');
    return `${planOnly ? '' : `<label>${esc(T('l.bulk_area'))} <select data-bulk="area">${areaOpts}</select></label>`}
      ${this.doc.floorPlans.length > 1 ? `<label>${esc(T('l.move_to_plan'))} <select data-bulk="plan">${planOpts}</select></label>` : ''}`;
  }

  private applyBulk(el: HTMLSelectElement) {
    if (el.value === '__keep') return;
    const tables = this.selectedItems().filter((i) => i.type === 'table');
    if (!tables.length) return;
    const before = this.snapshot();
    if (el.dataset.bulk === 'area') {
      for (const t of tables) t.ref.diningAreaId = el.value || null;
    } else if (el.dataset.bulk === 'plan') {
      const target = el.value;
      const used = new Set(this.doc.tables.filter((t: any) => t.floorPlanId === target).map((t: any) => String(t.label).toLowerCase()));
      for (const t of tables) {
        t.ref.floorPlanId = target;
        if (used.has(String(t.ref.label).toLowerCase())) t.ref.label = this.nextLabelFor(target);
        used.add(String(t.ref.label).toLowerCase());
      }
      this.selected.clear();
      this.planId = target;
    }
    this.commit(before);
    this.host.changed();
    this.render();
  }

  private onField(e: Event) {
    const el = e.target as HTMLInputElement;
    if (el.hasAttribute('data-lplan')) {
      if (el.value === '__new') {
        el.value = this.planId;
        const trigger = document.createElement('button');
        trigger.setAttribute('data-l', 'add-plan');
        this.el!.appendChild(trigger);
        trigger.click();
        trigger.remove();
        return;
      }
      this.planId = el.value;
      this.selected.clear();
      this.combo = null;
      return this.render();
    }
    if (el.dataset.bulk) return this.applyBulk(el as unknown as HTMLSelectElement);
    if (el.dataset.c && this.combo) {
      const c = this.doc.combos.find((x: any) => x.id === this.combo);
      if (!c) return;
      const before = this.snapshot();
      if (el.dataset.c === 'add') {
        if (el.value && !c.tableIds.includes(el.value)) c.tableIds.push(el.value);
      } else {
        const n = Math.max(1, Math.min(200, Math.round(Number(el.value)) || 1));
        c[el.dataset.c] = n;
        if (c.minParty > c.maxParty) {
          if (el.dataset.c === 'minParty') c.maxParty = c.minParty;
          else c.minParty = c.maxParty;
        }
      }
      this.commit(before);
      return this.render();
    }
    const f = el.dataset.f;
    if (!f || this.selected.size !== 1) return;
    const it = this.item([...this.selected][0]);
    if (!it) return;
    const r = it.ref;
    const before = this.snapshot();
    const n = Number(el.value);
    const isTable = it.type === 'table';
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(v) || lo));
    switch (f) {
      case 'label':
        if (isTable) r.label = el.value.trim() || r.label;
        break;
      case 'text':
        if (!isTable) r.text = el.value.trim().slice(0, 60) || null;
        break;
      case 'minSeats':
        r.minSeats = Math.max(1, Math.round(n) || 1);
        if (r.minSeats > r.maxSeats) r.maxSeats = r.minSeats;
        break;
      case 'maxSeats':
        r.maxSeats = Math.max(1, Math.round(n) || 1);
        if (r.minSeats > r.maxSeats) r.minSeats = r.maxSeats;
        break;
      case 'diningAreaId':
        r.diningAreaId = el.value || null;
        break;
      case 'shape':
        r.shape = el.value;
        break;
      case 'w':
        r.w = isTable ? clamp(n, MIN_SIZE, 600) : clamp(n, 4, 2000);
        if (isTable && !(r.h > 1)) r.h = it.h;
        break;
      case 'h':
        r.h = isTable ? clamp(n, MIN_SIZE, 600) : clamp(n, 4, 2000);
        if (isTable && !(r.w > 1)) r.w = it.w;
        break;
      case 'rotation':
        r.rotation = norm(n);
        break;
      case 'x':
        r.x = clamp(n, 0, MAX);
        break;
      case 'y':
        r.y = clamp(n, 0, MAX);
        break;
    }
    this.commit(before);
    this.render();
  }
}
